import { Component, ViewEncapsulation, ChangeDetectionStrategy, OnInit, DestroyRef, inject, ViewChild, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { DemoBlockService } from 'src/shared/services/demo-block.service';

import { AgreementDetailPage } from 'src/pages/compliance/agreement-detail/agreement-detail';
import { AddAgreementPage } from 'src/pages/compliance/add-agreement/add-agreement';

import { Agreement } from 'src/models/agreement.model';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

@Component({
  selector: 'renter-agreements-content',
  templateUrl: './renter-agreements-content.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RenterAgreementsContentComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private fb = inject(FormBuilder);
  private util = inject(Util);
  private demoBlock = inject(DemoBlockService);

  readonly windowService = inject(WindowService);

  form: UntypedFormGroup;
  headers: string[] = this.buildHeaders();
  allRows: any[] = [];
  shownRows: any[] = [];
  loaded = false;
  errorMessage: string;
  @ViewChild('tidyTable') tidyTable: any;
  private panelClosedSubscription: Subscription;

  private mockAgreements: Agreement[] = [
    { id: 1, name: 'Standard Lease Agreement', created_at: '2025-09-15', updated_at: '2026-01-20', status: 'active' },
    { id: 2, name: 'Short-Term Rental Agreement', created_at: '2025-11-03', updated_at: '2026-02-10', status: 'active' },
    { id: 3, name: 'Month-to-Month Rental Agreement', created_at: '2025-07-22', updated_at: '2025-12-05', status: 'active' },
    { id: 4, name: 'Pet Addendum', created_at: '2025-10-01', updated_at: '2026-01-15', status: 'active' },
    { id: 5, name: 'Move-In / Move-Out Checklist Agreement', created_at: '2026-01-08', updated_at: '2026-02-18', status: 'active' },
    { id: 6, name: 'Vacation Rental Agreement (2024)', created_at: '2024-06-10', updated_at: '2024-12-31', status: 'archived' },
  ];

  constructor() {
    this.form = this.fb.group({
      search: [''],
    });
    this.form.get('search')?.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => this.updateSearch(value));
  }

  async ngOnInit() {
    try {
      await this.loadPage();
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.util.showError(this.errorMessage, 10000);
    }
  }

  async loadPage() {
    this.headers = this.buildHeaders();
    const rows = this.buildRows(this.mockAgreements);
    this.allRows = rows;
    this.shownRows = rows;
    this.loaded = true;
  }

  buildHeaders(): string[] {
    return ['Agreement Name', 'Date Created', 'Date Modified', 'last-column'];
  }

  buildRows(items: Agreement[]): any[] {
    return items.map(item => ({
      'Agreement Name': { value: item.name },
      'Date Created': { value: item.created_at },
      'Date Modified': { value: item.updated_at },
      'last-column': { value: '', hasChevron: true },
      id: item.id,
    }));
  }

  updateSearch(search: string) {
    if (!search || search.length < 2) {
      this.shownRows = [...this.allRows];
      return;
    }
    const term = search.toLowerCase();
    this.shownRows = this.allRows.filter(row =>
      row['Agreement Name'].value.toLowerCase().includes(term)
    );
  }

  sortChanged(sort: any) {
    if (!sort.active || !sort.direction) {
      return;
    }
    this.shownRows = [...this.shownRows].sort((a, b) => {
      const valA = a[sort.active]?.value || '';
      const valB = b[sort.active]?.value || '';
      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      if (strA < strB) { return sort.direction === 'asc' ? -1 : 1; }
      if (strA > strB) { return sort.direction === 'asc' ? 1 : -1; }
      return 0;
    });
  }

  goToAddAgreement() {
    if (this.demoBlock.block()) { return; }
    this.goToPage('add-agreement', {}, AddAgreementPage);
  }

  onTableColumnClick(event: { column: string; element: any }) {
    if (this.demoBlock.block()) { return; }
    const agreement = this.mockAgreements.find(a => a.id === event.element.id);
    if (!agreement) {return;}
    const params = { agreement };
    this.goToPage('agreement-detail', params, AgreementDetailPage);
  }

  async goToPage(url: string, params: any, component: any) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(async () => {
        await this.loadPage();
      });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
